import type { JSX } from 'react';
import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import {
  BookOutlined,
  CameraOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  FlagOutlined,
  LinkOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';

import { Button, Tag } from '../ds';
import { friendlyError } from '../../lib/api';
import {
  OPENING_TASKS_KEYS,
  openingTasksApi,
  type MyCardTaskSetStatus,
  type MyOpeningPlanDto,
  type PartnerDatesPatch,
  type MyOpeningTasksDto,
} from '../../lib/api/openingTasks';
import { EquipmentPanel, EquipmentProgressTag, useMyEquipment } from './EquipmentPanel';
import { equipmentForTask } from './equipment-model';
import { withPlanChecklistItem, withPlanPartnerDates, withPlanTaskStatus } from '../opening-plan/plan-model';
import {
  carriedLabel,
  checkLinkUrl,
  checklistProgress,
  dueLabel,
  reviewerLabel,
  safeHttpUrl,
  shortLinkLabel,
  statusBadge,
  toggleTarget,
  workAction,
  partnerDatesError,
  partnerDatesText,
  ukSideLabel,
  withCardTaskStatus,
  withChecklistItem,
  withPartnerDates,
  type AnyCardTask,
} from './stage-tasks';
import { fmtDate } from './utils';

/** Подсказка на неактивной отметке в режиме «смотреть от лица»: менеджер только смотрит. */
const READ_ONLY_HINT = 'Режим просмотра — только чтение';

/** Внешняя ссылка из шаблона: на всякий случай только http(s) (сервер уже отфильтровал). */
const isHttp = (url: string): boolean => /^https?:\/\//i.test(url);

/**
 * Отметка с подписью («Сделано» / пункт чек-листа): настоящая кнопка-чекбокс, с клавиатуры и с телефона
 * (цель нажатия ≥ 32 px). Неактивная — серая, с подсказкой.
 */
function Check({
  checked,
  label,
  disabled,
  hint,
  onClick,
  children,
}: {
  checked: boolean;
  label: string;
  disabled: boolean;
  hint?: string;
  onClick: () => void;
  children: JSX.Element | string;
}): JSX.Element {
  return (
    <button
      type="button"
      role="checkbox"
      className="fk-ot-check"
      aria-checked={checked}
      aria-label={label}
      title={disabled ? hint : undefined}
      disabled={disabled}
      onClick={onClick}
    >
      <span className="fk-ot-check-box" data-checked={checked} aria-hidden="true">
        {checked ? <CheckOutlined /> : null}
      </span>
      <span className="fk-ot-check-text">{children}</span>
    </button>
  );
}

/** Снимок обоих кешей до оптимистичной правки — чтобы откатить при ошибке. */
interface CacheSnapshot {
  myTasks: MyOpeningTasksDto | undefined;
  plan: MyOpeningPlanDto | undefined;
}

/**
 * Одна и та же строка живёт и в «Моих задачах» (кеш `myTasks`), и в визарде (кеш `plan`): оптимистично правим
 * оба кеша, если они есть; откат — оба; после ответа сервера оба помечаются устаревшими.
 */
function useTaskCaches(): {
  apply: (fns: { myTasks: (d: MyOpeningTasksDto) => MyOpeningTasksDto; plan: (d: MyOpeningPlanDto) => MyOpeningPlanDto }) => Promise<CacheSnapshot>;
  rollback: (snap: CacheSnapshot | undefined) => void;
  settle: () => Promise<void>;
} {
  const qc = useQueryClient();
  return {
    apply: async (fns) => {
      await Promise.all([qc.cancelQueries({ queryKey: OPENING_TASKS_KEYS.myTasks }), qc.cancelQueries({ queryKey: OPENING_TASKS_KEYS.plan })]);
      const snap: CacheSnapshot = {
        myTasks: qc.getQueryData<MyOpeningTasksDto>(OPENING_TASKS_KEYS.myTasks),
        plan: qc.getQueryData<MyOpeningPlanDto>(OPENING_TASKS_KEYS.plan),
      };
      if (snap.myTasks) qc.setQueryData<MyOpeningTasksDto>(OPENING_TASKS_KEYS.myTasks, fns.myTasks(snap.myTasks));
      if (snap.plan) qc.setQueryData<MyOpeningPlanDto>(OPENING_TASKS_KEYS.plan, fns.plan(snap.plan));
      return snap;
    },
    rollback: (snap) => {
      if (snap?.myTasks) qc.setQueryData(OPENING_TASKS_KEYS.myTasks, snap.myTasks);
      if (snap?.plan) qc.setQueryData(OPENING_TASKS_KEYS.plan, snap.plan);
    },
    settle: async () => {
      await Promise.all([qc.invalidateQueries({ queryKey: OPENING_TASKS_KEYS.myTasks }), qc.invalidateQueries({ queryKey: OPENING_TASKS_KEYS.plan })]);
    },
  };
}

/**
 * Задача карточки открытия (v2): отметка «Сделано», срок (красным, если просрочено), пометки «с этапа …»,
 * «Веха», «Проверит …», «На доработке», у задач УК — «УК · отдел». Раскрывается: описание, чек-лист, статья базы
 * знаний, ссылки (`defaultOpen` — раскрыта сразу, для вкладки «Чеклисты»).
 * `readOnly` — режим «смотреть от лица»: всё видно, отмечать нельзя (сервер всё равно ответил бы 403).
 */
export function CardTaskRow({
  task,
  index,
  readOnly,
  defaultOpen = false,
  doableThisWeek = false,
}: {
  task: AnyCardTask;
  index: number;
  readOnly: boolean;
  defaultOpen?: boolean;
  /** Задачу «можно делать на этой неделе» (FB-32) — маленький ярлык рядом со статусом. */
  doableThisWeek?: boolean;
}): JSX.Element {
  const { message } = App.useApp();
  const caches = useTaskCaches();
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();

  const setStatus = useMutation({
    mutationFn: (next: MyCardTaskSetStatus) => openingTasksApi.setCardTaskStatus(task.id, next),
    onMutate: (next: MyCardTaskSetStatus) =>
      caches.apply({ myTasks: (d) => withCardTaskStatus(d, task.id, next), plan: (d) => withPlanTaskStatus(d, task.id, next) }),
    onError: (e, _next, snap) => {
      caches.rollback(snap);
      message.error(friendlyError(e));
    },
    onSettled: () => caches.settle(),
  });

  /** FB-32 шаг 2: даты «начали / закончим» — только для информации, план не меняют. */
  const [datesError, setDatesError] = useState<string | null>(null);
  const setDates = useMutation({
    mutationFn: (dates: PartnerDatesPatch) => openingTasksApi.setCardTaskPartnerDates(task.id, dates),
    onMutate: (dates: PartnerDatesPatch) =>
      caches.apply({ myTasks: (d) => withPartnerDates(d, task.id, dates), plan: (d) => withPlanPartnerDates(d, task.id, dates) }),
    onError: (e, _dates, snap) => {
      caches.rollback(snap);
      message.error(friendlyError(e));
    },
    onSettled: () => caches.settle(),
  });
  /** Смена одной из дат: пустое поле — очистить; окончание раньше начала не отправляем. */
  const onDate = (key: keyof PartnerDatesPatch, raw: string): void => {
    const value = raw === '' ? null : raw;
    const start = key === 'partnerStartDate' ? value : task.partnerStartDate;
    const due = key === 'partnerDueDate' ? value : task.partnerDueDate;
    const err = partnerDatesError(start, due);
    setDatesError(err);
    if (err) return;
    setDates.mutate({ [key]: value });
  };

  /** Пункт-«ссылка», для которого сейчас вводят адрес (inline, под пунктом). */
  const [linkEdit, setLinkEdit] = useState<{ index: number; value: string; error: string | null } | null>(null);

  const setItem = useMutation({
    mutationFn: (v: { index: number; done: boolean; url?: string }) => openingTasksApi.setCardChecklistItem(task.id, v.index, v.done, v.url),
    onMutate: (v: { index: number; done: boolean; url?: string }) =>
      caches.apply({
        myTasks: (d) => withChecklistItem(d, task.id, v.index, v.done, v.url),
        plan: (d) => withPlanChecklistItem(d, task.id, v.index, v.done, v.url),
      }),
    onError: (e, v, snap) => {
      caches.rollback(snap);
      const text = friendlyError(e);
      // Отказ по ссылке (например, не группа ВК) показываем под полем ввода — оно остаётся открытым.
      if (v.url !== undefined) setLinkEdit((cur) => (cur && cur.index === v.index ? { ...cur, error: text } : cur));
      else message.error(text);
    },
    onSuccess: (_r, v) => {
      if (v.url !== undefined) setLinkEdit(null);
    },
    onSettled: () => caches.settle(),
  });

  /** Клик по пункту: «ссылка» без адреса открывает поле ввода, остальное — отметка / снятие. */
  const onItem = (i: number, item: AnyCardTask['checklist'][number]): void => {
    if (item.kind === 'link' && !item.done) {
      setLinkEdit(linkEdit?.index === i ? null : { index: i, value: '', error: null });
      return;
    }
    setItem.mutate({ index: i, done: !item.done });
  };

  const saveLink = (): void => {
    if (!linkEdit || setItem.isPending) return;
    const checked = checkLinkUrl(linkEdit.value);
    if ('error' in checked) {
      setLinkEdit({ ...linkEdit, error: checked.error });
      return;
    }
    setItem.mutate({ index: linkEdit.index, done: true, url: checked.url });
  };

  const due = dueLabel(task);
  const badge = statusBadge(task.status);
  const work = workAction(task);
  const carried = carriedLabel(task);
  const ukSide = ukSideLabel(task);
  const reviewer = reviewerLabel(task);
  const progress = checklistProgress(task);
  const links = task.links.filter((l) => isHttp(l.url));
  const ownDates = partnerDatesText(task);
  // FB-34: у задачи «Заказ оборудования» внутри — спецификация (если УК её внесла).
  const hasEquipment = equipmentForTask(useMyEquipment().data, task.id) !== null;
  const hasDetails = !!task.description || task.checklist.length > 0 || !!task.kbArticle || links.length > 0 || task.canToggle || hasEquipment;
  const lockedHint = readOnly ? READ_ONLY_HINT : undefined;
  const mark = task.done ? <CheckOutlined /> : index + 1;

  return (
    <div className="fk-ot-ctask" data-done={task.done} data-overdue={task.overdue} data-rework={task.status === 'rework'}>
      <div className="fk-ot-ctask-row">
        <span className="fk-ot-num" data-done={task.done} aria-hidden="true">
          {mark}
        </span>
        <div className="fk-ot-ctask-main">
          {hasDetails ? (
            <button type="button" className="fk-ot-ctask-title" aria-expanded={open} aria-controls={bodyId} onClick={() => setOpen((o) => !o)}>
              <span>{task.title}</span>
              <DownOutlined aria-hidden="true" />
            </button>
          ) : (
            <div className="fk-ot-ctask-title" data-plain="true">
              <span>{task.title}</span>
            </div>
          )}
          <div className="fk-ot-ctask-meta">
            {badge ? (
              <Tag color={badge.tone} icon={badge.tone === 'red' ? <ExclamationCircleOutlined /> : undefined}>
                {badge.text}
              </Tag>
            ) : null}
            {due ? (
              <span className="fk-ot-due" data-overdue={due.overdue}>
                <ClockCircleOutlined aria-hidden="true" /> {due.text}
              </span>
            ) : null}
            {task.isMilestone ? (
              <Tag color="indigo" icon={<FlagOutlined />}>
                Веха
              </Tag>
            ) : null}
            {ukSide ? (
              <Tag color="indigo" title="Эту задачу выполняет управляющая компания">
                {ukSide}
              </Tag>
            ) : null}
            {doableThisWeek && !task.done ? (
              <Tag color="orange" title="Предшественники выполнены, начало — не позже конца этой недели">
                Можно на этой неделе
              </Tag>
            ) : null}
            {carried ? <Tag color="gold">{carried}</Tag> : null}
            {reviewer ? (
              <Tag color="blue" icon={<SafetyCertificateOutlined />} title="Выполнение принимает проверяющий">
                {reviewer}
              </Tag>
            ) : null}
            {hasEquipment ? <EquipmentProgressTag taskId={task.id} /> : null}
            {progress.total > 0 ? (
              <span>
                чек-лист {progress.done}/{progress.total}
              </span>
            ) : null}
            {ownDates ? <span title="Даты, которые вы указали сами. Только для информации — план не меняют">{ownDates}</span> : null}
            {task.done && task.doneAt ? <span>выполнено {fmtDate(task.doneAt)}</span> : null}
          </div>
        </div>
        {task.canToggle ? (
          <div className="fk-ot-ctask-act">
            {work ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={readOnly || setStatus.isPending}
                title={readOnly ? READ_ONLY_HINT : undefined}
                aria-label={`${work.label}: ${task.title}`}
                onClick={() => setStatus.mutate(work.target)}
              >
                {work.label}
              </Button>
            ) : null}
            <Check
              checked={task.done}
              label={`${task.done ? 'Снять отметку «Сделано»' : 'Сделано'}: ${task.title}`}
              disabled={readOnly || setStatus.isPending}
              hint={lockedHint}
              onClick={() => setStatus.mutate(toggleTarget(task))}
            >
              Сделано
            </Check>
          </div>
        ) : null}
      </div>

      {open && hasDetails ? (
        <div id={bodyId} className="fk-ot-ctask-body">
          {task.description ? <p className="fk-ot-ctask-desc">{task.description}</p> : null}
          {task.canToggle ? (
            <div className="fk-ot-dates">
              <div className="fk-ot-dates-row">
                <label className="fk-ot-dates-field">
                  <span>Начали</span>
                  <input
                    className="fk-ot-input"
                    type="date"
                    min="2020-01-01"
                    max="2035-12-31"
                    value={task.partnerStartDate ?? ''}
                    disabled={readOnly || setDates.isPending}
                    title={lockedHint}
                    onChange={(e) => onDate('partnerStartDate', e.target.value)}
                  />
                </label>
                <label className="fk-ot-dates-field">
                  <span>Закончим</span>
                  <input
                    className="fk-ot-input"
                    type="date"
                    min="2020-01-01"
                    max="2035-12-31"
                    value={task.partnerDueDate ?? ''}
                    disabled={readOnly || setDates.isPending}
                    title={lockedHint}
                    aria-invalid={!!datesError}
                    onChange={(e) => onDate('partnerDueDate', e.target.value)}
                  />
                </label>
              </div>
              {datesError ? (
                <div className="fk-ot-cl-err" role="alert">
                  {datesError}
                </div>
              ) : null}
              <div className="fk-ot-dates-hint">Даты для информации: их увидит управляющая компания, а план и сроки задач они не меняют.</div>
            </div>
          ) : null}
          {hasEquipment ? <EquipmentPanel taskId={task.id} readOnly={readOnly} /> : null}
          {task.checklist.length > 0 ? (
            <ul className="fk-ot-cl" aria-label="Чек-лист задачи">
              {task.checklist.map((item, i) => {
                const href = item.kind === 'link' && item.done ? safeHttpUrl(item.url) : null;
                const editing = item.kind === 'link' && linkEdit?.index === i;
                const locked = readOnly || !task.canToggle || setItem.isPending;
                return (
                  <li key={`${i}-${item.title}`}>
                    <Check
                      checked={item.done}
                      label={`${item.title}${item.kind === 'photo' ? ' (фото)' : item.kind === 'link' ? ' (ссылка)' : ''}`}
                      disabled={locked}
                      hint={lockedHint}
                      onClick={() => onItem(i, item)}
                    >
                      {item.title}
                    </Check>
                    {item.kind === 'photo' ? (
                      <span className="fk-ot-cl-photo" title="Фото-пункт: достаточно отметки">
                        <CameraOutlined aria-hidden="true" /> фото
                      </span>
                    ) : null}
                    {item.kind === 'link' && !item.done && !editing ? (
                      <span className="fk-ot-cl-photo" title="Пункт отмечается вместе со ссылкой">
                        <LinkOutlined aria-hidden="true" /> нужна ссылка
                      </span>
                    ) : null}
                    {item.kind === 'link' && item.done ? (
                      <span className="fk-ot-cl-link">
                        <LinkOutlined aria-hidden="true" />
                        {href ? (
                          <a href={href} target="_blank" rel="noopener noreferrer" title={href}>
                            {shortLinkLabel(href)}
                          </a>
                        ) : (
                          <span className="fk-ot-muted">ссылка не сохранена</span>
                        )}
                        {!locked ? (
                          <button type="button" className="fk-ot-cl-x" aria-label={`Снять отметку и ссылку: ${item.title}`} title="Снять отметку и ссылку" onClick={() => setItem.mutate({ index: i, done: false })}>
                            <CloseOutlined aria-hidden="true" />
                          </button>
                        ) : null}
                      </span>
                    ) : null}
                    {editing && linkEdit ? (
                      <div className="fk-ot-cl-edit">
                        <div className="fk-ot-cl-edit-row">
                          <input
                            className="fk-ot-input"
                            type="url"
                            inputMode="url"
                            autoFocus
                            value={linkEdit.value}
                            maxLength={600}
                            placeholder="Вставьте ссылку"
                            aria-label={`Ссылка: ${item.title}`}
                            aria-invalid={!!linkEdit.error}
                            onChange={(e) => setLinkEdit({ ...linkEdit, value: e.target.value, error: null })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                saveLink();
                              } else if (e.key === 'Escape') setLinkEdit(null);
                            }}
                          />
                          <Button variant="primary" size="sm" loading={setItem.isPending} onClick={saveLink}>
                            Сохранить
                          </Button>
                          <Button variant="text" size="sm" disabled={setItem.isPending} onClick={() => setLinkEdit(null)}>
                            Отмена
                          </Button>
                        </div>
                        {linkEdit.error ? (
                          <div className="fk-ot-cl-err" role="alert">
                            {linkEdit.error}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}
          {task.kbArticle || links.length > 0 ? (
            <div className="fk-ot-ctask-links">
              {task.kbArticle ? (
                <Link to={`/article/${task.kbArticle.slug}`}>
                  <BookOutlined aria-hidden="true" /> {task.kbArticle.title}
                </Link>
              ) : null}
              {links.map((l, i) => (
                <a key={`${i}-${l.url}`} href={l.url} target="_blank" rel="noopener noreferrer">
                  <LinkOutlined aria-hidden="true" /> {l.title}
                </a>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
